import {
  all,
  call,
  debounce,
  put,
  select,
  takeLatest,
  takeEvery,
  delay,
  fork,
  take,
  actionChannel,
  flush,
} from 'redux-saga/effects';

import {
  bulkUpdateVisualization as bulkUpdateVisualizationApi,
  updateVisualization as updateVisualizationApi,
  deleteVisualization as deleteVisualizationApi,
  createVisualization as createVisualizationApi,
  getVisualizations as getVisualizationsApi,
  getSeries,
  updateVisualizationsTags,
  removeVisualizationFromLayouts,
  updateVisualizationInLayouts,
  uploadVisualizationsExcel as uploadVisualizationsExcelApi,
  downloadVisualizationsTemplate,
  getVisualizationTemplateTypes as getVisualizationTemplateTypesApi,
} from '../../services';
import { getCachedResponse, setCachedResponse } from '../../responseCache';
import {
  CLEAR_SITE_DATA,
  REFRESH_VALUES,
  setPollingActive,
  setPollingActiveDone,
  getPollingDateRange,
  getRangeFromLabel,
  getRangeFromPartial,
  afterSiteCleared,
} from '../application';
import { requestStateSets } from '../statesets';
import { getRawSourceConnectivityById, requestSiteVariables } from '../sources';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import {
  CREATE_PANEL_VISUALIZATIONS_SUBSCRIPTION,
  GET_SINGLE_VISUALIZATION_DATA,
  REQUEST_DELETE_VISUALIZATION,
  REQUEST_VISUALIZATIONS,
  SAVE_VISUALIZATION,
  LAST_VALUE_VISUALIZATIONS,
  BULK_UPSERT_VISUALIZATION,
  EXPORT_VISUALIZATIONS_TEMPLATE,
  UPLOAD_VISUALIZATIONS_EXCEL,
  REQUEST_VISUALIZATION_TEMPLATE_TYPES,
  requestVisualizations,
  getSingleVisualization,
  deleteVisualizationSuccess,
  receiveVisualization,
  receiveVisualizations,
  receivedValues,
  setLoading,
  setVisualizationsImportedAt,
  setImportErrors,
  receiveVisualizationTemplateTypes,
} from './actions';
import { downloadBlob, sanitizeFileName } from '../../utils';
import {
  getVisualization,
  getVisualizationSubscriptions,
  getVisualizationValues,
} from './selectors';
import {
  isFailedValue,
  getFailedVariableIds,
  FAILED_VARIABLE_RETRY_DELAYS_MS,
} from '../variables/helpers';
import config from '../../config';

const { onPremise } = config;

// Normalize aggregate names - treat 'avg' and 'mean' as equivalent
function normalizeAggregate(agg) {
  const normalized = (agg || 'avg').toLowerCase();
  // Convert 'avg' to 'mean' for consistency with backend
  return normalized === 'avg' ? 'mean' : normalized;
}

// Extract aggregate from a response value object.
// The API may not include an `aggregate` property -> fall back to
// parsing it from the "name" field which is always {uuid}.{aggregate}.
function getValueAggregate(value) {
  return value.aggregate || value.name?.split('.')?.pop();
}

// isFailedValue/getFailedVariableIds/FAILED_VARIABLE_RETRY_DELAYS_MS come from
// variables/helpers.js - shared by both bundles' fetching sagas.

// Response cache
const responseCache = {
  series: new Map(),
};

export function clearVisualizationResponseCache() {
  responseCache.series.clear();
}

// ─── Per-variable cache helpers ─────────────────────────────────────
// Cache each variable's response individually instead of one monolithic key.
// This enables partial cache hits — if 19/20 variables are cached, only 1 is fetched.

// Short, fixed-length key: "sr|uuid|mean|1234567890|1234567899|60s|linear" (~83 chars)
function getSeriesVariableKey(variableId, aggregate, from, to, interval, fill) {
  return `series|${variableId}|${normalizeAggregate(aggregate)}|${from}|${to}|${interval || 'none'}|${fill || 'none'}`;
}

// Check per-variable series cache — returns cached values and uncached variables
function getSeriesFromCache(variables, from, to, interval) {
  const cachedValues = [];
  const uncachedVariables = [];

  for (const v of variables) {
    const key = getSeriesVariableKey(v.id, v.aggregate, from, to, interval, v.fill);
    const data = getCachedResponse(responseCache.series, key);
    if (data) {
      cachedValues.push(...data);
    } else {
      uncachedVariables.push(v);
    }
  }

  return { cachedValues, uncachedVariables };
}

// Split API response and cache each variable's data separately.
// Accepts the variables array to build a per-variable fill lookup,
// since the API response doesn't include the fill that was requested.
function cacheSeriesResponse(responseValues, variables, from, to, interval) {
  // Build fill lookup: variable+aggregate → fill
  const fillMap = new Map();
  for (const v of variables) {
    const mapKey = `${v.id}_${normalizeAggregate(v.aggregate)}`;
    fillMap.set(mapKey, v.fill);
  }

  const grouped = new Map();
  for (const value of responseValues) {
    if (isFailedValue(value)) continue; // don't cache a failed chunk as if it were real data
    const groupKey = `${value.variable}_${normalizeAggregate(getValueAggregate(value))}`;
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, []);
    }
    grouped.get(groupKey).push(value);
  }

  for (const [groupKey, values] of grouped) {
    if (values.length > 0) {
      const variableId = values[0].variable;
      const agg = getValueAggregate(values[0]);
      const fill = fillMap.get(groupKey);
      const key = getSeriesVariableKey(variableId, agg, from, to, interval, fill);
      setCachedResponse(responseCache.series, key, values);
    }
  }
}

// Patches recovered series values (from a retry) into a visualization's already-dispatched
// values, replacing only the entries that previously failed - other variables are left as-is.
function* mergeRecoveredSeriesValues(visualizationId, panelId, recoveredValues) {
  if (recoveredValues.length === 0) return;

  const allValues = yield select(getVisualizationValues);
  const panelEntry = (allValues[visualizationId] || []).find((item) => item.panelId === panelId);
  const existingValues = panelEntry?.seriesValues || [];

  const recoveredByKey = new Map(
    recoveredValues.map((value) => [
      `${value.variable}_${normalizeAggregate(getValueAggregate(value))}`,
      value,
    ])
  );

  const mergedValues = existingValues.map((value) => {
    const key = `${value.variable}_${normalizeAggregate(getValueAggregate(value))}`;
    return recoveredByKey.get(key) || value;
  });

  yield put(receivedValues(visualizationId, { seriesValues: mergedValues }, panelId));
}

// Silently retries just the variables whose chunk failed, patching recovered data back into
// each affected visualization as it arrives. Most chunk failures are transient, so this usually
// resolves invisibly; if retries are exhausted, the failed placeholders are left as-is.
function* retryFailedSeriesVariables(
  failedVariables,
  connection,
  from,
  to,
  interval,
  panelId,
  actions
) {
  let remaining = failedVariables;

  for (const delayMs of FAILED_VARIABLE_RETRY_DELAYS_MS) {
    if (remaining.length === 0) return;
    yield delay(delayMs);

    let response;
    try {
      response = yield call(getSeries, { variables: remaining, connection, from, to, interval });
    } catch {
      continue; // network-level failure on the retry itself - try again on the next delay
    }

    const stillFailingIds = new Set(getFailedVariableIds(response.values));
    const recovered = response.values.filter((value) => !isFailedValue(value));
    const recoveredVariables = remaining.filter((v) => !stillFailingIds.has(v.id));

    if (recovered.length > 0) {
      cacheSeriesResponse(recovered, recoveredVariables, from, to, interval);
      for (const { visualization } of actions) {
        yield call(mergeRecoveredSeriesValues, visualization.id, panelId, recovered);
      }
    }

    remaining = remaining.filter((v) => stillFailingIds.has(v.id));
  }
}

// Batch window: collect actions arriving within 50ms into one batch
const BATCH_WINDOW_MS = 50;

// Group actions by panelId so each panel is batched independently
function groupActionsByPanel(actions) {
  const panelGroups = new Map();
  for (const action of actions) {
    const { panelId } = action;
    if (!panelGroups.has(panelId)) {
      panelGroups.set(panelId, []);
    }
    panelGroups.get(panelId).push(action);
  }
  return panelGroups;
}

// Batch processor using actionChannel — groups by panel before processing
function* batchedVisualizationDataFetcher() {
  const channel = yield actionChannel(GET_SINGLE_VISUALIZATION_DATA);

  while (true) {
    const firstAction = yield take(channel);
    const batch = [firstAction];

    // Check if there are already queued actions - if so, collect immediately
    let buffered = yield flush(channel);

    // If queue was empty, wait brief window for simultaneous requests
    if (buffered.length === 0) {
      yield delay(BATCH_WINDOW_MS);
      buffered = yield flush(channel);
    }

    batch.push(...buffered);

    // Group by panelId so each panel gets its own request with correct date range
    const panelGroups = groupActionsByPanel(batch);

    // Process each panel group independently (parallel)
    // Always use batching (even for single viz) for consistent cache behavior
    yield all(
      [...panelGroups.entries()].map(([, panelActions]) =>
        call(processBatchedVisualizationData, panelActions)
      )
    );
  }
}

// Process a batch of visualization data requests
function* processBatchedVisualizationData(actions, bypassCache = false) {
  // Only series visualizations are processed here; last-value visualizations
  // are subscribed directly through the variables bundle.
  const seriesActions = [];

  for (const action of actions) {
    const { visualization } = action;
    const viz =
      visualization || (yield select((state) => getVisualization(state, action.visualizationId)));

    if (!viz) continue;

    if (!LAST_VALUE_VISUALIZATIONS.has(viz.type)) {
      seriesActions.push({ action, visualization: viz });
    }
  }

  // Process series batch
  if (seriesActions.length > 0) {
    yield call(processBatchedSeries, seriesActions, bypassCache);
  }
}

// Process batched series requests — all actions belong to the same panel
function* processBatchedSeries(actions, bypassCache = false) {
  const allVariables = [];

  // All actions share the same panelId — resolve date range once
  const { panelId } = actions[0].action;
  const dateRange = yield select((state) => getPollingDateRange(state, panelId));

  const relativeAdjustedRange = dateRange?.label
    ? getRangeFromLabel(dateRange.label)
    : getRangeFromPartial(dateRange);

  const { startDate: from, endDate: to, granularity } = relativeAdjustedRange;
  const granularityValue =
    typeof granularity === 'string' ? Number.parseInt(granularity, 10) : granularity;
  const interval = granularityValue > 0 ? `${granularityValue}s` : undefined;

  for (const { visualization } of actions) {
    const { variables: rawVariables } = visualization;

    yield put(setLoading(visualization.id, panelId));

    const variables = (rawVariables || []).map((config) => {
      const { aggregate: aggValue, ...rest } = config;
      rest.aggregate = aggValue === 'none' ? 'avg' : aggValue;
      return rest;
    });

    // Deduplicate variables across visualizations in this panel.
    // Include fill in dedup — same variable with different fill returns different data.
    for (const v of variables) {
      if (
        !allVariables.some(
          (existing) =>
            existing.id === v.id && existing.aggregate === v.aggregate && existing.fill === v.fill
        )
      ) {
        allVariables.push(v);
      }
    }
  }

  const connection = yield select(getRawSourceConnectivityById);

  // Validate date range
  if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
    for (const { visualization } of actions) {
      yield put(setLoading(visualization.id, panelId, false));
    }
    return;
  }

  try {
    let allValues;

    // Polling a relative range always fetches fresh (its from/to move every poll); absolute ranges reuse the cache.
    if (bypassCache && dateRange?.label) {
      const response = yield call(getSeries, {
        variables: allVariables,
        connection,
        from,
        to,
        interval,
      });
      cacheSeriesResponse(response.values, allVariables, from, to, interval);
      allValues = response.values;
    } else {
      // Per-variable cache — only fetch uncached variables
      const { cachedValues, uncachedVariables } = getSeriesFromCache(
        allVariables,
        from,
        to,
        interval
      );

      let fetchedValues = [];

      if (uncachedVariables.length > 0) {
        const response = yield call(getSeries, {
          variables: uncachedVariables,
          connection,
          from,
          to,
          interval,
        });
        cacheSeriesResponse(response.values, uncachedVariables, from, to, interval);
        fetchedValues = response.values;
      }

      allValues = [...cachedValues, ...fetchedValues];
    }

    // Distribute results to each visualization (both cache hit and miss)
    for (const { visualization } of actions) {
      const vizVariables = new Set(
        visualization.variables.map((v) => {
          const agg = v.aggregate === 'none' ? 'avg' : v.aggregate;
          return `${v.id}_${normalizeAggregate(agg)}`;
        })
      );

      const filteredValues = allValues.filter((value) => {
        const key = `${value.variable}_${normalizeAggregate(getValueAggregate(value))}`;
        return vizVariables.has(key);
      });

      yield put(receivedValues(visualization.id, { seriesValues: filteredValues }, panelId));
    }

    // cachedValues never contain failed placeholders (cacheSeriesResponse skips them), so any
    // failed entry here came from this fetch - retry those variables in the background.
    const failedVariableIds = getFailedVariableIds(allValues);
    if (failedVariableIds.length > 0) {
      const failedVariables = allVariables.filter((v) => failedVariableIds.includes(v.id));
      yield fork(
        retryFailedSeriesVariables,
        failedVariables,
        connection,
        from,
        to,
        interval,
        panelId,
        actions
      );
    }
  } catch (error) {
    yield call(checkOnline);

    // Handle error for all visualizations in batch
    for (const { visualization } of actions) {
      yield put(setLoading(visualization.id, panelId, false));
    }
    // Show a single notification for the batch (generic message)
    yield put(displayNotification(getNotification('getVisualizations', 'error')(error.message)));
  }
}

export function* doSaveVisualization(action) {
  const { visualization, parentPanelId } = action;
  if (!visualization) return;

  try {
    let response;

    const originalViz =
      visualization.id && !visualization.id.includes('temporary')
        ? yield select((state) => state.visualizations.originalVisualizations[visualization.id])
        : null;

    const cleanedViz = {
      ...visualization,
      variables: visualization.variables.map(({ aggregate = 'none', ...rest }) => ({
        ...rest,
        aggregate,
      })),
    };
    if (!visualization.id || visualization.id.includes('temporary')) {
      const temporaryId = visualization.id;
      delete visualization.id;
      response = yield call(createVisualizationApi, cleanedViz);
      yield put(deleteVisualizationSuccess(temporaryId));
    } else {
      response = yield call(updateVisualizationApi, visualization.id, cleanedViz);

      if (onPremise && originalViz && originalViz.name !== visualization.name) {
        try {
          yield call(
            updateVisualizationInLayouts,
            visualization.site,
            originalViz.name,
            visualization.name
          );
        } catch (layoutError) {
          console.warn('Failed to update visualization in layouts:', layoutError);
        }
      }
    }

    yield put(receiveVisualization(response));
    // yeild series data - to avoid reload on select/unselect checkbox in viz panel
    if (parentPanelId) yield put(getSingleVisualization(response, parentPanelId));
    yield put(displayNotification(getNotification('saveVisualization', 'success')()));
  } catch (error) {
    console.error('Unable to save visualization:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('saveVisualization', 'error')()));
  }
}

export function* doBulkSaveVisualization(action) {
  const { visualization } = action;
  if (!visualization) return;

  try {
    yield call(bulkUpdateVisualizationApi, visualization);
    yield put(requestVisualizations(visualization.site));
    yield put(displayNotification(getNotification('saveVisualization', 'success')()));
  } catch {
    yield call(checkOnline);
    yield put(displayNotification(getNotification('saveVisualization', 'error')()));
  }
}

export function* doUpdateVisualizationsTags(action) {
  const { id: tagId, site, org } = action;
  if (!tagId) yield put(displayNotification(getNotification('deleteTag', 'warning')()));

  try {
    yield call(updateVisualizationsTags, { tagId, site, org });
    yield put(requestVisualizations(site));
  } catch (error) {
    yield call(checkOnline);
    console.error('Unable to update visualizations tags', error);
  }
}

export function* doDeleteVisualization(action) {
  const { visualizationId } = action;
  try {
    const visualization = yield select((state) => getVisualization(state, visualizationId));

    yield call(deleteVisualizationApi, visualizationId);

    if (onPremise) {
      try {
        yield call(removeVisualizationFromLayouts, visualization.site, visualization.name);
      } catch (layoutError) {
        console.warn('Failed to remove visualization from layouts:', layoutError);
      }
    }

    yield put(deleteVisualizationSuccess(visualizationId));
    yield put(
      displayNotification(getNotification('deleteVisualization', 'success')(visualizationId))
    );
  } catch (error) {
    console.error('Unable to delete visualization:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(getNotification('deleteVisualization', 'error')(visualizationId))
    );
  }
}

export function* doRequestVisualizations(action) {
  const { siteId } = action;
  try {
    const { values } = yield call(getVisualizationsApi, { site: siteId });
    yield put(receiveVisualizations(values, siteId));
  } catch (error) {
    console.error('Unable to fetch visualizations:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVisualizations', 'error')()));
    yield put(receiveVisualizations([], siteId));
  }
}

// panel-level subscription, batch fetch all visualizations
export function* doGetPanelVisualizations(action) {
  const { visualizations, panelId } = action;

  // This will be picked up by the batchedVisualizationDataFetcher
  for (const visualization of visualizations) {
    yield put(getSingleVisualization(visualization, panelId));
  }
}

export function* doUpdateAllImmediately(action) {
  // Panels fetch their own data on mount.
  if (action?.isInitialLoad) return;

  yield put(setPollingActive('visualizations'));
  const subscriptions = yield select(getVisualizationSubscriptions);
  const subscriptionIds = Object.keys(subscriptions);
  const values = yield select(getVisualizationValues);
  const visualizations = yield all(
    subscriptionIds.map((id) => select((state) => getVisualization(state, id)))
  );

  // Collect all visualization actions
  const actions = [];
  for (const visualization of visualizations) {
    if (!visualization) continue;
    for (const item of values[visualization.id] || []) {
      actions.push({
        visualization,
        panelId: item.panelId,
      });
    }
  }

  // Group by panel and process each independently (parallel, bypass cache)
  if (actions.length > 0) {
    const panelGroups = groupActionsByPanel(actions);
    yield all(
      [...panelGroups.entries()].map(([, panelActions]) =>
        call(processBatchedVisualizationData, panelActions, true)
      )
    );
  }

  yield put(setPollingActiveDone('visualizations'));
}

export function* doUploadVisualizationsExcel(action) {
  const { file, siteId, org, sourceId } = action;
  try {
    yield put(setVisualizationsImportedAt(0));
    const fd = new FormData();
    fd.append('file', file);
    fd.append('site', siteId);
    fd.append('org', org);
    fd.append('sourceId', sourceId);
    const {
      importErrors = [],
      statesetsAdded,
      visualizationsAdded,
    } = yield call(uploadVisualizationsExcelApi, fd);
    if (importErrors.length > 0) {
      yield put(setImportErrors(importErrors));
    } else {
      yield put(requestSiteVariables());
      yield put(requestVisualizations(siteId));
      yield put(requestStateSets(siteId));
      yield put(setVisualizationsImportedAt(Date.now()));
      const message = `Added ${visualizationsAdded} visualization${visualizationsAdded === 1 ? '' : 's'} and ${statesetsAdded} stateset${statesetsAdded === 1 ? '' : 's'}`;
      yield put(
        displayNotification(getNotification('uploadVisualizationsExcel', 'success')(message))
      );
    }
  } catch (error) {
    console.error('Unable to upload visualizations excel:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('uploadVisualizationsExcel', 'error')()));
  }
}

export function* doDownloadVisualizationsTemplate(action) {
  const { site, templateType, sourceId, sourceName, options } = action;
  try {
    const file = yield call(downloadVisualizationsTemplate, {
      site: site.id,
      org: site.org,
      sourceId,
      templates: [templateType],
      ...options,
    });
    downloadBlob(
      file.data,
      `${sanitizeFileName(site.name)}-${sanitizeFileName(sourceName)}-signals_visualizations_template_${new Date().toISOString().replaceAll(/[:.]/g, '-')}.xlsx`,
      file.headers['content-type']
    );
  } catch (error) {
    console.error('Unable to download visualizations template excel:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('downloadVisualizationsExcel', 'error')()));
  }
}

export function* doRequestVisualizationTemplateTypes() {
  try {
    const templateTypes = yield call(getVisualizationTemplateTypesApi);
    yield put(receiveVisualizationTemplateTypes(templateTypes));
  } catch (error) {
    console.error('Unable to fetch visualization template types:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVisualizationTemplateTypes', 'error')()));
  }
}

export function* doClearVisualizationResponseCache() {
  yield call(clearVisualizationResponseCache);
}

export const sagas = [
  takeEvery(SAVE_VISUALIZATION, doSaveVisualization),
  takeLatest(BULK_UPSERT_VISUALIZATION, doBulkSaveVisualization),
  takeLatest(REQUEST_VISUALIZATIONS, afterSiteCleared(doRequestVisualizations)),
  takeLatest(REQUEST_DELETE_VISUALIZATION, doDeleteVisualization),
  takeEvery(CREATE_PANEL_VISUALIZATIONS_SUBSCRIPTION, doGetPanelVisualizations),
  takeLatest(EXPORT_VISUALIZATIONS_TEMPLATE, doDownloadVisualizationsTemplate),
  takeLatest(UPLOAD_VISUALIZATIONS_EXCEL, doUploadVisualizationsExcel),
  takeLatest(REQUEST_VISUALIZATION_TEMPLATE_TYPES, doRequestVisualizationTemplateTypes),
  fork(batchedVisualizationDataFetcher),
  debounce(500, REFRESH_VALUES, doUpdateAllImmediately),
  takeEvery(CLEAR_SITE_DATA, doClearVisualizationResponseCache),
];

export { getCachedResponse, setCachedResponse } from '../../responseCache';
export {
  normalizeAggregate,
  getValueAggregate,
  getSeriesVariableKey,
  getSeriesFromCache,
  cacheSeriesResponse,
  groupActionsByPanel,
  processBatchedVisualizationData,
  processBatchedSeries,
  retryFailedSeriesVariables,
};
export { isFailedValue, getFailedVariableIds } from '../variables/helpers';
